'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';
import type { ITopBanner } from '@/sanity/lib/definitions';
import { resolveI18nText } from '@/sanity/globals/I18n';
import { resolvePageLink } from '@/sanity/globals/CTA';

/**
 * Pieces the two seasonal top banners share with each other (and, in
 * markup, with TopBannerV1): the [[...]] copy split, the deferred CTA link
 * and the white pill.
 */

export interface TextSegment {
  text: string;
  highlighted: boolean;
}

/**
 * The banner copy uses the same [[...]] markers as the other banners:
 * "[[80% OFF]] Instant Funding & Challenges". Odd indexes of the split are
 * the highlighted segments, even indexes the plain ones; empty segments are
 * dropped.
 */
export function parseTextSegments(text: string): TextSegment[] {
  return text
    .split(/\[\[(.+?)\]\]/g)
    .map((part, index) => ({ text: part.trim(), highlighted: index % 2 === 1 }))
    .filter((segment) => segment.text.length > 0);
}

/** The [[...]] part(s) of the copy and the plain part(s), each joined into one line. */
export function splitCopy(segments: TextSegment[]): { highlighted: string; plain: string } {
  const join = (highlighted: boolean) =>
    segments
      .filter((segment) => segment.highlighted === highlighted)
      .map((segment) => segment.text)
      .join(' ');
  return { highlighted: join(true), plain: join(false) };
}

/**
 * The banner's copy, label and CTA in the page language, [[..]] already
 * split. `mobileSegments` is the Mobile Text when the editor filled it in,
 * otherwise the same segments as the desktop copy.
 */
export function useBannerCopy(data: ITopBanner, language: string | undefined) {
  const preText = resolveI18nText(data.preText, data.preTextI18n, language);
  // stegaClean before the regex split: in draft mode Sanity interleaves
  // invisible characters into the string and `[[` would no longer match.
  const text = stegaClean(resolveI18nText(data.text, data.textI18n, language)) ?? '';
  const mobileText = stegaClean(resolveI18nText(data.mobileText, data.mobileTextI18n, language)) ?? '';
  const ctaLabel = resolveI18nText(data.ctaLabel, data.ctaLabelI18n, language);

  // Same deferred link resolution as TopBannerClassic / TopBannerV1.
  const link = data.ctaLink ? resolvePageLink(data.ctaLink, language) : null;
  const [finalLink, setFinalLink] = useState('');
  useEffect(() => {
    if (!link) return;
    setFinalLink(link);
  }, [link]);

  const segments = parseTextSegments(text);
  const mobileSegments = mobileText.trim() ? parseTextSegments(mobileText) : segments;
  return { preText, segments, mobileSegments, ctaLabel, finalLink };
}

interface BannerPillProps {
  href: string;
  label: string | undefined;
  /** Accessible name when the editor left the label empty (WCAG 2.4.4 / 4.1.2). */
  fallbackLabel: string;
  className?: string;
}

/**
 * Figma 13041:6397 / 12770:24996 "Link" — white pill, radius 35, gap 8,
 * Geist SemiBold 14/21 -0.14 #04231a, 18px arrow; 8px vertical padding on
 * desktop, 7 on the phone frames (13047:25262 / 13047:25295). The side
 * padding differs per design (Halloween 22/18, Black Month 16/16) and comes
 * in `className`.
 */
export function BannerPill({ href, label, fallbackLabel, className }: BannerPillProps) {
  return (
    <Link
      href={href}
      aria-label={label ? undefined : fallbackLabel}
      className={cn(
        'flex w-fit items-center gap-[1rem] rounded-full bg-white px-[2rem] py-[0.875rem] transition-opacity hover:opacity-80 lg:py-[1rem]',
        className,
      )}
    >
      {label && (
        <span className="whitespace-nowrap font-geist text-[1.75rem] font-semibold leading-[2.625rem] tracking-[-0.0175rem] text-[#04231A]">
          {label}
        </span>
      )}
      {/* eslint-disable-next-line @next/next/no-img-element -- static icon */}
      <img src="/promo-v1/arrow.svg" alt="" aria-hidden className="h-[2.25rem] w-[2.25rem] shrink-0" />
    </Link>
  );
}
